import React, { useState, useCallback, useRef, useLayoutEffect, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '../hooks/useMediaQuery';
import BottomDrawer from './BottomDrawer';

const InfoDisplay = ({ content, children, theme, maxWidthClass = 'max-w-xs', drawerTitle }) => {
    const { t } = useTranslation();
    const [isDesktopTooltipVisible, setDesktopTooltipVisible] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0, below: false, arrow: 0 });
    const tipId = useId();

    const triggerRef = useRef(null);
    const tooltipRef = useRef(null);
    const isMobile = useMediaQuery('(max-width: 768px)');

    const updatePosition = useCallback(() => {
        if (!triggerRef.current || !tooltipRef.current) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const PADDING = 16; 

        // Position above the trigger by default
        let left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
        let top = triggerRect.top - tooltipRect.height - 10; 

        // If tooltip would go off the left edge
        if (left < PADDING) {
            left = PADDING;
        }

        // If tooltip would go off the right edge
        if (left + tooltipRect.width > viewportWidth - PADDING) {
            left = viewportWidth - tooltipRect.width - PADDING;
        }
        
        // If tooltip would go off the top, position below instead
        let below = false;
        if (top < PADDING) {
            top = triggerRect.bottom + 10;
            below = true;
        }
        
        // Ensure tooltip doesn't go off the bottom
        if (top + tooltipRect.height > viewportHeight - PADDING) {
            top = viewportHeight - tooltipRect.height - PADDING;
        }
        
        // the arrow keeps pointing at the icon wherever the box was clamped to
        const arrow = triggerRect.left + triggerRect.width / 2 - left;
        setPosition({ top, left, below, arrow });
    }, []);

    useLayoutEffect(() => {
        if (isDesktopTooltipVisible && tooltipRef.current) {
            // Use requestAnimationFrame to ensure DOM is updated
            requestAnimationFrame(() => {
                updatePosition();
            });
        }
    }, [isDesktopTooltipVisible, updatePosition]);
    
    useEffect(() => {
        if (isDesktopTooltipVisible) {
            const handleResize = () => updatePosition();
            const handleScroll = () => updatePosition();
            window.addEventListener('resize', handleResize);
            window.addEventListener('scroll', handleScroll, true);
            return () => {
                window.removeEventListener('resize', handleResize);
                window.removeEventListener('scroll', handleScroll, true);
            };
        }
    }, [isDesktopTooltipVisible, updatePosition]);


    const handleMouseEnter = useCallback(() => {
        if (!isMobile) {
            setDesktopTooltipVisible(true);
        }
    }, [isMobile]);

    const handleMouseLeave = useCallback(() => {
        if (!isMobile) {
            setDesktopTooltipVisible(false);
        }
    }, [isMobile]);

    // Hover is not the only way in: a tap (an iPad, a touch laptop) or keyboard
    // focus opens it too; leaving, blur and Escape close it. The click never reaches what the icon sits
    // in, so an ⓘ inside a suggestion chip explains without applying the chip.
    const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isMobile) setIsDrawerOpen(true);
        else setDesktopTooltipVisible(true);
    };

    useEffect(() => {
        if (!isDesktopTooltipVisible) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setDesktopTooltipVisible(false); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isDesktopTooltipVisible]);
    
    const handleCloseDrawer = () => {
        setIsDrawerOpen(false);
    };

    const finalDrawerTitle = drawerTitle || t('general.help');

    const DesktopTooltip = () => (
        isDesktopTooltipVisible && typeof document !== 'undefined' && createPortal(
            <div
                ref={tooltipRef}
                id={tipId}
                role="tooltip"
                className={`tooltip ${position.top !== 0 ? 'open' : ''} ${position.below ? 'below' : ''}`}
                style={{
                    top: position.top !== 0 ? `${position.top}px` : '-9999px',
                    left: position.top !== 0 ? `${position.left}px` : '-9999px',
                    '--arrow-x': `${position.arrow}px`,
                    maxWidth: maxWidthClass === 'max-w-xs' ? '320px' : maxWidthClass === 'max-w-sm' ? '384px' : '448px',
                }}
                onMouseEnter={() => setDesktopTooltipVisible(true)}
                onMouseLeave={() => setDesktopTooltipVisible(false)}
            >
                {content}
            </div>,
            document.body
        )
    );

    return (
        <>
            <span
                ref={triggerRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onFocus={handleMouseEnter}
                onBlur={handleMouseLeave}
                onClick={handleClick}
                aria-describedby={isDesktopTooltipVisible ? tipId : undefined}
                style={{
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                }}
                aria-label={finalDrawerTitle}
            >
                {children}
            </span>
            
            <BottomDrawer isOpen={isDrawerOpen} onClose={handleCloseDrawer} title={finalDrawerTitle} theme={theme}>
                <div style={{ padding: '16px' }}>{content}</div>
            </BottomDrawer>
            
            {!isMobile && <DesktopTooltip />}
        </>
    );
};

export default InfoDisplay;